import React, { useState, useMemo, useEffect } from 'react'
import { Row, Col, Card, Input, Typography, Tag, message, Layout, Tree, Spin, Space, Button } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { useNavigate } from 'react-router-dom'
import { 
  SearchOutlined, 
  BookOutlined, 
  FileTextOutlined,
  MedicineBoxOutlined,
  RobotOutlined,
  ToolOutlined,
  GlobalOutlined,
} from '@ant-design/icons'
import { 
  EDUCATION_LEVEL_MAP, 
  getAllTextbooks,
  fetchTextbooksFromAPI,
  getEducationLevel,
  getCategory,
  type TextbookInfo,
} from '../../utils/textbookParser'
import './Knowledge.css'

const { Title, Paragraph } = Typography
const { Sider, Content } = Layout

type CategoryKey = 'culture' | 'philosophy' | 'tcmClinical' | 'herbal' | 'westernClinical' | 'intelligent' | 'equipment' | 'other'
type Category = { name: string; icon: React.ReactNode; color: string; textbooks: string[] }
type CategoryMap = Record<CategoryKey, Category>

// 动态生成教育层次与分类（优先用后端数据，失败回退本地映射）
const buildEducationLevels = (list: TextbookInfo[]) => {
  const levels: Record<string, { name: string; textbooks: string[] }> = {
    undergraduate: { name: '本科阶段', textbooks: [] },
    master: { name: '硕士阶段', textbooks: [] },
    doctoral: { name: '博士阶段', textbooks: [] },
    postdoctoral: { name: '博后探索阶段', textbooks: [] },
    professionalInsight: { name: '专业感悟阶段', textbooks: [] },
  }
  if (list.length === 0) {
    Object.entries(EDUCATION_LEVEL_MAP).forEach(([k, v]) => {
      if (levels[k]) levels[k].textbooks = v || []
    })
    return levels
  }
  list.forEach(book => {
    const level = book.educationLevel || getEducationLevel(book.name) || 'undergraduate'
    if (!levels[level]) return
    levels[level].textbooks.push(book.name)
  })
  Object.keys(levels).forEach(k => {
    levels[k].textbooks = Array.from(new Set(levels[k].textbooks)).sort()
  })
  return levels
}

const buildCategories = (list: TextbookInfo[]): CategoryMap => {
  const base: CategoryMap = {
    culture: { name: '文化', icon: <GlobalOutlined />, color: '#52C41A', textbooks: [] as string[] },
    philosophy: { name: '哲学', icon: <BookOutlined />, color: '#1890FF', textbooks: [] as string[] },
    tcmClinical: { name: '中医临床', icon: <MedicineBoxOutlined />, color: '#F5222D', textbooks: [] as string[] },
    herbal: { name: '中药', icon: <MedicineBoxOutlined />, color: '#FA8C16', textbooks: [] as string[] },
    westernClinical: { name: '西医临床', icon: <MedicineBoxOutlined />, color: '#722ED1', textbooks: [] as string[] },
    intelligent: { name: '智能', icon: <RobotOutlined />, color: '#13C2C2', textbooks: [] as string[] },
    equipment: { name: '设备', icon: <ToolOutlined />, color: '#EB2F96', textbooks: [] as string[] },
    other: { name: '其他', icon: <FileTextOutlined />, color: '#8C8C8C', textbooks: [] as string[] },
  }
  if (list.length === 0) {
    return base
  }
  list.forEach(book => {
    const cat = book.category || getCategory(book.name) || 'other'
    const catKey: CategoryKey = (cat in base ? (cat as CategoryKey) : 'other')
    const bucket = base[catKey]?.textbooks
    if (bucket) bucket.push(book.name)
  })
  ;(Object.keys(base) as CategoryKey[]).forEach((k: CategoryKey) => {
    base[k].textbooks = Array.from(new Set(base[k].textbooks)).sort()
  })
  return base
}

const Knowledge: React.FC = () => {
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)
  const [textbooks, setTextbooks] = useState<TextbookInfo[]>([])
  const educationLevels = useMemo(() => buildEducationLevels(textbooks), [textbooks])
  const categoriesMap = useMemo(() => buildCategories(textbooks), [textbooks])

  // 从数据库/API加载教材列表（用于未来扩展）
  useEffect(() => {
    const loadTextbooks = async () => {
      setLoading(true)
      try {
        // 优先从API获取（如果API可用），设置5秒超时
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)
        
        try {
          const apiTextbooks = await fetchTextbooksFromAPI()
          clearTimeout(timeoutId)
          if (apiTextbooks.length > 0) {
            console.log('从API加载教材:', apiTextbooks.length, '本')
            setTextbooks(apiTextbooks)
            setLoading(false)
            return
          }
        } catch (apiError) {
          clearTimeout(timeoutId)
          console.warn('API加载失败，使用本地配置:', apiError)
        }
        
        // 如果API不可用或超时，使用本地配置
        const localTextbooks = getAllTextbooks().map(name => ({
          name,
          tableName: name,
          educationLevel: getEducationLevel(name),
          category: getCategory(name),
        }))
        console.log('使用本地配置教材:', localTextbooks.length, '本')
        setTextbooks(localTextbooks)
      } catch (error) {
        console.error('加载教材列表失败:', error)
        // 使用本地配置作为后备
        const localTextbooks = getAllTextbooks().map(name => ({
          name,
          tableName: name,
          educationLevel: getEducationLevel(name),
          category: getCategory(name),
        }))
        console.log('使用本地配置教材:', localTextbooks.length, '本')
        setTextbooks(localTextbooks)
      } finally {
        setLoading(false)
      }
    }
    loadTextbooks()
  }, [])

  const handleSearch = (value: string) => {
    if (!value.trim()) {
      message.warning('请输入搜索关键词')
      return
    }
    // TODO: 实现搜索功能
    message.info(`搜索: ${value}`)
  }

  const handleTextbookClick = (textbookName: string) => {
    // TODO: 跳转到教材详情页面
    message.info(`查看教材: ${textbookName}`)
  }

  // 构建树形结构数据（按教育层次分类）
  const treeData: DataNode[] = useMemo(() => {
    return Object.entries(educationLevels).map(([levelKey, level]) => {
      const categoryNodes: DataNode[] = []
      
      Object.entries(categoriesMap).forEach(([categoryKey, category]) => {
        // 过滤出属于当前教育层次的教材
        const levelTextbooks = category.textbooks.filter(book => 
          level.textbooks.includes(book)
        )
        
        if (levelTextbooks.length > 0) {
          categoryNodes.push({
            title: (
              <span>
                {category.icon}
                <span style={{ marginLeft: 8 }}>{category.name}</span>
                <Tag color={category.color} style={{ marginLeft: 8 }}>
                  {levelTextbooks.length}
                </Tag>
              </span>
            ),
            key: `level-${levelKey}-category-${categoryKey}`,
            children: levelTextbooks.map((textbook, index) => ({
              title: textbook,
              key: `level-${levelKey}-category-${categoryKey}-textbook-${index}`,
              isLeaf: true,
            })),
          })
        }
      })

      return {
        title: (
          <span>
            <BookOutlined />
            <span style={{ marginLeft: 8, fontWeight: 600 }}>{level.name}</span>
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {level.textbooks.length}
            </Tag>
          </span>
        ),
        key: `level-${levelKey}`,
        children: categoryNodes,
      }
    })
  }, [educationLevels, categoriesMap])

  // 过滤树形数据（根据搜索关键词）
  const filteredTreeData = useMemo(() => {
    if (!searchValue.trim()) {
      return treeData
    }

    const filterTree = (nodes: DataNode[]): DataNode[] => {
      return nodes
        .map((node) => {
          const title = typeof node.title === 'string' 
            ? node.title 
            : (node.title as React.ReactElement)?.props?.children?.[1]?.props?.children || ''
          
          const matches = title.toLowerCase().includes(searchValue.toLowerCase())
          
          if (matches) {
            return node
          }
          
          if (node.children) {
            const filteredChildren = filterTree(node.children)
            if (filteredChildren.length > 0) {
              return {
                ...node,
                children: filteredChildren,
              }
            }
          }
          
          return null
        })
        .filter((node): node is DataNode => node !== null)
    }

    return filterTree(treeData)
  }, [treeData, searchValue])

  // 原来的分类模块数据
  const categories = [
    { id: 1, name: '经典文献', count: 128, icon: <BookOutlined /> },
    { id: 2, name: '方剂库', count: 256, icon: <FileTextOutlined /> },
    { id: 3, name: '中药库', count: 512, icon: <BookOutlined /> },
    { id: 4, name: '体质库', count: 64, icon: <FileTextOutlined /> },
    { id: 5, name: '诊疗经验', count: 89, icon: <BookOutlined /> },
    { id: 6, name: '现代研究', count: 156, icon: <FileTextOutlined /> },
  ]

  const onSelect = (selectedKeysValue: React.Key[]) => {
    setSelectedKeys(selectedKeysValue)
    if (selectedKeysValue.length > 0) {
      const key = selectedKeysValue[0] as string
      if (key.includes('textbook-')) {
        const parts = key.split('-')
        // 格式: level-{levelKey}-category-{categoryKey}-textbook-{index}
        const levelKey = parts[1]
        const categoryKey = parts[3]
        const index = parseInt(parts[5])
        const level = educationLevels[levelKey as keyof typeof educationLevels]
        const category = categoriesMap[categoryKey as keyof typeof categoriesMap]
        if (level && category && level.textbooks.includes(category.textbooks[index])) {
          handleTextbookClick(category.textbooks[index])
        }
      }
    }
  }

  return (
    <div className="knowledge-page">
      {/* 左侧固定树状结构（导航栏下方） */}
      <Layout className="knowledge-layout">
        <Sider width={300} className="knowledge-sider">
          <div className="tree-header">
            <Title level={4}>智能中医教材</Title>
            {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
          </div>
          <div className="tree-container">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin />
                <div style={{ marginTop: 16, color: '#999' }}>加载教材列表...</div>
              </div>
            ) : (
              <Tree
                showLine
                defaultExpandAll
                selectedKeys={selectedKeys}
                onSelect={onSelect}
                treeData={filteredTreeData}
                className="knowledge-tree"
              />
            )}
          </div>
        </Sider>
        
        {/* 右侧内容区域 */}
        <Content className="knowledge-content-wrapper">
          <div className="knowledge-hero">
            <div className="section-container-right">
              <Title level={1} className="page-title">中医药知识树</Title>
              <Paragraph className="page-subtitle">
                汇聚千年中医智慧，整合经典文献与现代研究，构建全面的中医药知识体系
              </Paragraph>
              <div className="search-wrapper">
                <Space.Compact style={{ maxWidth: 600, width: '100%' }}>
                  <Input
                    placeholder="搜索知识库内容..."
                    size="large"
                    style={{ width: 'calc(100% - 40px)' }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onPressEnter={(e) => handleSearch((e.target as HTMLInputElement).value)}
                    allowClear
                  />
                  <Button
                    size="large"
                    icon={<SearchOutlined />}
                    onClick={() => handleSearch(searchValue)}
                    type="primary"
                  />
                </Space.Compact>
              </div>
            </div>
          </div>

          {/* 搜索框下方的分类模块 */}
          <div className="section-container-right">
            <Title level={2} className="section-title">知识分类</Title>
            <Row gutter={[24, 24]}>
              {categories.map((category) => (
                <Col xs={24} sm={12} lg={8} key={category.id}>
                  <Card
                    className="category-card"
                    hoverable
                    onClick={() => {
                      // 跳转到具体分类页面
                      if (category.id === 3) {
                        // 中药库
                        navigate('/knowledge/herbs')
                      } else {
                        console.log('Navigate to category:', category.id)
                      }
                    }}
                  >
                    <div className="category-icon">{category.icon}</div>
                    <Title level={4} className="category-name">{category.name}</Title>
                    <div className="category-count">
                      <Tag color="blue">{category.count} 条</Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {/* 内容展示区域 */}
          <div className="section-container-right">
                    <div className="content-placeholder" />
          </div>
        </Content>
      </Layout>
    </div>
  )
}

export default Knowledge

